import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, rateFor, costAccountsFor } from '../../api/accounts';

let segmentSeq = 0;
function emptySegment() {
  return { key: `seg-${++segmentSeq}`, airline: '', pnr: '', route_from: '', route_to: '' };
}

export default function TicketForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAgent, agentRefId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isView = !!id;

  const [ticket, setTicket] = useState(null);
  const [clientLabel, setClientLabel] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [commTiers, setCommTiers] = useState([]);
  const [commBlocks, setCommBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState('');
  const [form, setForm] = useState({
    client_id: '', ticket_number: '',
    departure_date: '', return_date: '',
    cabin_class: 'ECONOMY', cost_price: '', cost_currency_id: '',
    supplier_id: '', supplier_label: '',
    sell_price: '', sell_currency_id: '', exchange_rate: '1',
    payment_status: 'UNPAID', notes: '',
    care_of_agent_id: '', care_of_agent_label: '',
    agent_ref_id: '', agent_ref_label: '', agent_commission: '',
  });
  const [segments, setSegments] = useState([emptySegment()]);
  const addSegment = () => setSegments(s => [...s, emptySegment()]);
  const removeSegment = (key) => setSegments(s => s.filter(seg => seg.key !== key));
  const updateSegment = (key, field, val) => setSegments(s => s.map(seg => seg.key === key ? { ...seg, [field]: val } : seg));
  const [lineItems, setLineItems] = useState([{ description: '', unitPrice: '', qty: 1 }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code').then(({ data }) => {
      setCurrencies(data || []);
      if (!id) { const lkr = (data || []).find(c => c.code === 'LKR'); if (lkr) { set('cost_currency_id', lkr.id); set('sell_currency_id', lkr.id); } }
    });
    supabase.from('suppliers').select('id, name, supplier_type').eq('is_active', true).order('name').then(({ data }) => setSuppliers(data || []));
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => {
      const mapped = (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
      setAgents(mapped);
      if (isAgent && agentRefId && !id) {
        const me = mapped.find(a => a.id === agentRefId);
        if (me) setForm(f => ({ ...f, care_of_agent_id: me.id, care_of_agent_label: me.label, agent_ref_id: me.id, agent_ref_label: me.label }));
      }
    });
    supabase.from('commission_tables').select('*').eq('product_type', 'Ticket').eq('is_active', true).order('gp_from').then(({ data }) => {
      setCommTiers(data || []);
      const blocks = [...new Set((data || []).map(t => t.rule_name).filter(Boolean))];
      setCommBlocks(blocks);
    });
    if (id) supabase.from('tickets').select('*, clients(full_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => setTicket(data));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Strip commas when parsing — inputs store formatted strings
  const stripCommas = v => parseFloat(String(v).replace(/,/g, '')) || 0;
  const fmtComma = n => {
    const num = parseFloat(String(n).replace(/,/g, ''));
    if (!num && num !== 0) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const totalPax = lineItems.length;
  const totalSell = lineItems.reduce((s, it) => s + stripCommas(it.unitPrice), 0);
  const totalCost = stripCommas(form.cost_price) * totalPax;
  const gp = totalSell - totalCost;
  const gpPct = totalSell > 0 ? ((gp / totalSell) * 100).toFixed(1) : 0;
  const netGp = gp - stripCommas(form.agent_commission);
  const netGpPct = totalSell > 0 ? ((netGp / totalSell) * 100).toFixed(1) : 0;

  const updateLineItem = (i, field, value) => {
    setLineItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const blurLineItem = (i, field) => {
    setLineItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const formatted = fmtComma(it[field]);
      return formatted ? { ...it, [field]: formatted } : it;
    }));
  };
  const focusLineItem = (i, field) => {
    setLineItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const raw = stripCommas(it[field]);
      return { ...it, [field]: raw ? String(raw) : '' };
    }));
  };
  const addLineItem = () => setLineItems(prev => [...prev, { description: '', ticketNumber: '', unitPrice: '', qty: 1 }]);
  const removeLineItem = (i) => setLineItems(prev => prev.filter((_, idx) => idx !== i));

  // Auto-calculate commission from tiers when GP% and agent are set
  const calcAutoComm = (sellPrice, costPrice, agentId, blockOverride) => {
    const block = blockOverride !== undefined ? blockOverride : selectedBlock;
    const activeTiers = block ? commTiers.filter(t => t.rule_name === block) : commTiers;
    if (!agentId || !activeTiers.length) return '';
    const sell = parseFloat(sellPrice || 0);
    const cost = parseFloat(costPrice || 0);
    if (sell <= 0) return '';
    const gpAmt = sell - cost;
    const gpPctVal = (gpAmt / sell) * 100;
    const tier = activeTiers.slice().sort((a, b) => parseFloat(b.gp_from) - parseFloat(a.gp_from))
      .find(t => {
        const cmp = t.commission_type === 'PERCENT' ? gpPctVal : gpAmt;
        return cmp >= parseFloat(t.gp_from) && (t.gp_to == null || cmp < parseFloat(t.gp_to));
      });
    if (!tier) return '';
    if (tier.fixed_amount != null && parseFloat(tier.fixed_amount) > 0) return String(parseFloat(tier.fixed_amount).toFixed(2));
    const basis = tier.commission_basis || 'ON_GP';
    const base = basis === 'ON_GP' ? gpAmt : basis === 'ON_SELL' ? sell : cost;
    return String((base * parseFloat(tier.commission_percent || 0) / 100).toFixed(2));
  };

  const setWithAutoComm = (k, v) => {
    setForm(f => {
      const updated = { ...f, [k]: v };
      if (k === 'cost_price' || k === 'agent_ref_id') {
        updated.agent_commission = calcAutoComm(totalSell, updated.cost_price, updated.agent_ref_id);
      }
      return updated;
    });
  };

  // Re-run whenever block, agent, or prices change — catches any ordering of user input
  useEffect(() => {
    if (!form.agent_ref_id || parseFloat(form.sell_price) <= 0 || !commTiers.length) return;
    const comm = calcAutoComm(form.sell_price, form.cost_price, form.agent_ref_id);
    if (comm !== '') setForm(f => ({ ...f, agent_commission: comm }));
  }, [selectedBlock, form.agent_ref_id, totalSell, form.cost_price, commTiers]); // intentional: calcAutoComm is stable given these deps

  const handleSubmit = async () => {
    const validSegments = segments.filter(s => s.airline).map(({ key, ...s }) => s);
    if (!form.client_id || !validSegments.length) return setError('Client and at least one segment airline required');
    setSaving(true); setError('');
    try {
      const ticket_ref = 'TK' + Date.now().toString().slice(-7);
      // Legacy flat airline/pnr/route columns mirror the first segment (many reports,
      // journals, and printouts still read these directly) — segments holds the full itinerary.
      const { data, error: err } = await supabase.from('tickets').insert({
        ticket_ref,
        client_id: form.client_id,
        care_of_agent_id: form.care_of_agent_id || null,
        airline: validSegments[0]?.airline || '',
        pnr: validSegments[0]?.pnr || '',
        segments: validSegments,
        ticket_number: form.ticket_number,
        route_from: validSegments[0]?.route_from || '',
        route_to: validSegments[validSegments.length - 1]?.route_to || '',
        departure_date: form.departure_date || null,
        return_date: form.return_date || null,
        cabin_class: form.cabin_class,
        cost_price: totalCost || null,
        cost_currency_id: form.cost_currency_id || null,
        supplier_id: form.supplier_id || null,
        sell_price: totalSell || null,
        sell_currency_id: form.sell_currency_id || null,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        gross_profit: gp || null,
        gp_margin_percent: parseFloat(gpPct) || null,
        payment_status: form.payment_status,
        ticket_status: 'ACTIVE',
        notes: form.notes,
        created_by: username,
      }).select().single();
      if (err) throw err;

      const sellCode = currencies.find(c => c.id === form.sell_currency_id)?.code;
      const costCode = currencies.find(c => c.id === form.cost_currency_id)?.code;

      // Cost journal (cost currency): DR Airline Ticket Cost / CR Airline Ticket Payable
      if (totalCost > 0) {
        const { expenseAccount, payableAccount } = costAccountsFor('Ticket', costCode);
        await postJournal({
          date: new Date().toISOString().slice(0, 10),
          description: `Ticket cost: ${ticket_ref} - ${validSegments[0]?.airline || ''}`,
          reference_type: 'TICKET_COST',
          reference_id: data.id,
          currency_id: form.cost_currency_id || null,
          exchange_rate: rateFor(costCode, form.exchange_rate),
          lines: [
            { account_id: expenseAccount, debit_amount: totalCost, credit_amount: 0,          description: `Cost - ${ticket_ref}` },
            { account_id: payableAccount, debit_amount: 0,         credit_amount: totalCost,  description: `Payable - ${ticket_ref}`, party_type: form.supplier_id ? 'supplier' : null, party_id: form.supplier_id || null },
          ],
          created_by: username,
        });
      }

      // Commission: recognise expense at sale (matching principle), track payment status separately
      if (form.agent_ref_id && form.agent_commission) {
        const commAmt = parseFloat(form.agent_commission);
        if (commAmt > 0) {
          await postJournal({
            date: new Date().toISOString().slice(0, 10),
            description: `Agent commission: Ticket ${data.id?.slice(0, 8)}`,
            reference_type: 'TICKET_COMMISSION',
            reference_id: data.id,
            exchange_rate: 1,
            lines: [
              { account_id: ACCOUNTS.COMMISSION_EXPENSE, debit_amount: commAmt, credit_amount: 0, description: 'Commission expense' },
              { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: 0, credit_amount: commAmt, description: 'Commission payable', party_type: 'agent', party_id: form.agent_ref_id },
            ],
            created_by: username,
          });
          await supabase.from('commissions').insert({
            agent_ref_id: form.agent_ref_id,
            reference_type: 'TICKET',
            reference_id: data.id,
            product_type: 'Ticket',
            gp_amount: gp || 0,
            gp_percent: parseFloat(gpPct) || 0,
            commission_amount: commAmt,
            commission_basis: 'FIXED_AMOUNT',
            commission_status: 'PENDING',
          });
        }
      }
      navigate(`/tickets/${data.id}`);
    } catch (err) { setError(err.message || 'Error'); setSaving(false); }
  };

  const openEdit = () => {
    setSegments(ticket.segments?.length ? ticket.segments.map(s => ({ ...s, key: emptySegment().key })) : [
      { ...emptySegment(), airline: ticket.airline || '', pnr: ticket.pnr || '', route_from: ticket.route_from || '', route_to: ticket.route_to || '' },
    ]);
    setEditForm({
      ticket_number: ticket.ticket_number || '',
      departure_date: ticket.departure_date ? ticket.departure_date.slice(0, 16) : '',
      cabin_class: ticket.cabin_class || 'ECONOMY',
      cost_price: ticket.cost_price != null ? String(ticket.cost_price) : '',
      cost_currency_id: ticket.cost_currency_id || '',
      sell_price: ticket.sell_price != null ? String(ticket.sell_price) : '',
      sell_currency_id: ticket.sell_currency_id || '',
      exchange_rate: ticket.exchange_rate != null ? String(ticket.exchange_rate) : '1',
      payment_status: ticket.payment_status || 'UNPAID',
      notes: ticket.notes || '',
    });
    setEditing(true);
  };

  const setEF = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleSaveEdit = async () => {
    setSaving(true); setError('');
    try {
      const costPrice = parseFloat(String(editForm.cost_price).replace(/,/g, '')) || null;
      const sellPrice = parseFloat(String(editForm.sell_price).replace(/,/g, '')) || null;
      const gp = (sellPrice || 0) - (costPrice || 0);
      const gpPct = sellPrice > 0 ? ((gp / sellPrice) * 100) : null;
      const validSegments = segments.filter(s => s.airline).map(({ key, ...s }) => s);
      await supabase.from('tickets').update({
        airline: validSegments[0]?.airline || '',
        pnr: validSegments[0]?.pnr || '',
        segments: validSegments,
        ticket_number: editForm.ticket_number || null,
        route_from: validSegments[0]?.route_from || null,
        route_to: validSegments[validSegments.length - 1]?.route_to || null,
        departure_date: editForm.departure_date || null,
        cabin_class: editForm.cabin_class,
        cost_price: costPrice,
        cost_currency_id: editForm.cost_currency_id || null,
        sell_price: sellPrice,
        sell_currency_id: editForm.sell_currency_id || null,
        exchange_rate: parseFloat(editForm.exchange_rate) || 1,
        gross_profit: gp || null,
        gp_margin_percent: gpPct || null,
        payment_status: editForm.payment_status,
        notes: editForm.notes || null,
        updated_by: username,
      }).eq('id', id);
      const { data: refreshed } = await supabase.from('tickets')
        .select('*, clients(full_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)')
        .eq('id', id).single();
      setTicket(refreshed);
      setEditing(false);
    } catch (err) { setError(err.message || 'Error saving'); }
    setSaving(false);
  };

  const handleVoid = async () => {
    if (!window.confirm('Void this ticket? All journal entries (sale, cost, commission) will be reversed.')) return;
    await supabase.from('tickets').update({ ticket_status: 'VOID' }).eq('id', id);
    // Reverse all journals for this ticket (sale, cost, commission)
    const { data: jes } = await supabase.from('journal_entries').select('id')
      .in('reference_type', ['TICKET', 'TICKET_COST', 'TICKET_COMMISSION'])
      .eq('reference_id', id).eq('status', 'POSTED').eq('is_reversed', false);
    for (const je of jes || []) await voidJournal(je.id, 'Ticket voided', username);
    // Cancel the commission record so it doesn't show as payable
    await supabase.from('commissions').update({ commission_status: 'CANCELLED' })
      .eq('reference_type', 'TICKET').eq('reference_id', id);
    supabase.from('tickets').select('*, clients(full_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code)').eq('id', id).single().then(({ data }) => setTicket(data));
  };

  if (isView) {
    if (!ticket) return <div className="loading-screen"><div className="spinner" /></div>;
    return (
      <div>
        <div className="page-header">
          <div><div className="page-title">{ticket.ticket_ref}</div><div className="page-subtitle">{ticket.clients?.full_name} · {ticket.airline}</div></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={async () => {
              const { data: existing } = await supabase.from('invoices').select('id').eq('source_id', ticket.id).eq('invoice_type', 'INVOICE').neq('status', 'VOID').limit(1);
              if (existing && existing.length > 0) { navigate(`/invoices/${existing[0].id}`); return; }
              const yy = String(new Date().getFullYear()).slice(-2);
              const { data: maxRow } = await supabase.from('invoices').select('invoice_number').ilike('invoice_number', `TLS-${yy}-%`).order('invoice_number', { ascending: false }).limit(1);
              const lastSeq = maxRow?.[0]?.invoice_number ? parseInt(maxRow[0].invoice_number.split('-').pop()) : 0;
              const num = `TLS-${yy}-${String(lastSeq + 1).padStart(4, '0')}`;
              const sell = parseFloat(ticket.sell_price || 0);
              const segSub = (ticket.segments?.length ? ticket.segments : [])
                .map(s => `${s.airline}${s.pnr ? ` (${s.pnr})` : ''}: ${s.route_from} → ${s.route_to}`).join('\n');
              const sub = segSub || [ticket.ticket_number ? `Ticket: ${ticket.ticket_number}` : null, ticket.pnr ? `PNR: ${ticket.pnr}` : null, ticket.route_from && ticket.route_to ? `Route: ${ticket.route_from} → ${ticket.route_to}` : null].filter(Boolean).join(' · ');
              const lineItems = [{ description: `Flight Ticket${ticket.airline ? ` — ${ticket.airline}` : ''}`, sub: sub || null, qty: 1, unitPrice: sell, amount: sell }];
              const today = new Date().toISOString().slice(0, 10);
              const { data: inv } = await supabase.from('invoices').insert({
                invoice_number: num, invoice_type: 'INVOICE', client_id: ticket.client_id,
                source_type: 'ticket', source_id: ticket.id, line_items: lineItems,
                issued_date: today, status: 'DRAFT', created_by: username,
              }).select().single();
              if (sell > 0) {
                const je = await postJournal({
                  date: today, description: `Invoice ${num}`, reference_type: 'INVOICE_TICKET', reference_id: inv.id,
                  currency_id: ticket.sell_currency_id || null, exchange_rate: rateFor(ticket.sell_currency?.code, ticket.exchange_rate || 1),
                  lines: [
                    { account_id: ACCOUNTS.AR, debit_amount: sell, credit_amount: 0, description: `AR - ${num}`, party_type: 'client', party_id: ticket.client_id },
                    { account_id: ACCOUNTS.TICKET_REVENUE, debit_amount: 0, credit_amount: sell, description: `Revenue - ${num}` },
                  ],
                  created_by: username,
                });
                if (je?.id) await supabase.from('invoices').update({ journal_entry_id: je.id }).eq('id', inv.id);
              }
              navigate(`/invoices/${inv.id}`);
            }}>Generate Invoice</button>
            {ticket.ticket_status === 'ACTIVE' && !editing && (
              <button className="btn btn-secondary" onClick={openEdit}>Edit</button>
            )}
            {ticket.ticket_status === 'ACTIVE' && !editing && (
              <button className="btn btn-secondary" onClick={handleVoid}>Void Ticket</button>
            )}
          </div>
        </div>

        {error && <div style={{ color: 'var(--danger)', marginBottom: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}

        {editing ? (
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, color: 'var(--primary)' }}>Edit Ticket</div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Ticket Number</label><input className="form-input" value={editForm.ticket_number} onChange={e => setEF('ticket_number', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Departure</label><input className="form-input" type="datetime-local" value={editForm.departure_date} onChange={e => setEF('departure_date', e.target.value)} /></div>

              <div className="form-group full-width" style={{ marginTop: 4 }}>
                <label className="form-label">Flight Segments *</label>
                {segments.map((seg, i) => (
                  <div key={seg.key} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr 0.8fr auto', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                    <TypeSearch
                      fetchFn={async (q) => {
                        if (!q || q.length < 2) return [];
                        const { data } = await supabase.from('airlines').select('iata_code, name').or(`name.ilike.%${q}%,iata_code.ilike.%${q}%`).eq('is_active', true).limit(12);
                        return (data || []).map(a => ({ ...a, id: a.iata_code, label: `${a.name} (${a.iata_code})` }));
                      }}
                      value={seg.airline}
                      displayKey="label"
                      placeholder={`Segment ${i + 1} airline`}
                      minChars={2}
                      onSelect={a => updateSegment(seg.key, 'airline', `${a.name} (${a.iata_code})`)}
                      onClear={() => updateSegment(seg.key, 'airline', '')}
                    />
                    <input className="form-input" value={seg.pnr} onChange={e => updateSegment(seg.key, 'pnr', e.target.value)} placeholder="PNR" />
                    <input className="form-input" value={seg.route_from} onChange={e => updateSegment(seg.key, 'route_from', e.target.value.toUpperCase())} placeholder="From (CMB)" />
                    <input className="form-input" value={seg.route_to} onChange={e => updateSegment(seg.key, 'route_to', e.target.value.toUpperCase())} placeholder="To (DXB)" />
                    {segments.length > 1 && (
                      <button type="button" onClick={() => removeSegment(seg.key)}
                        style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={addSegment}>+ Add Segment</button>
              </div>
              <div className="form-group">
                <label className="form-label">Cabin Class</label>
                <select className="form-input" value={editForm.cabin_class} onChange={e => setEF('cabin_class', e.target.value)}>
                  {['ECONOMY','PREMIUM_ECONOMY','BUSINESS','FIRST'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {!isAgent && (<>
                <div className="form-group full-width" style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Purchase Details</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Cost Price</label>
                  <input className="form-input" inputMode="decimal" value={editForm.cost_price}
                    onChange={e => setEF('cost_price', e.target.value.replace(/[^0-9.]/g, ''))}
                    onBlur={e => { const f = fmtComma(e.target.value); if (f) setEF('cost_price', f); }}
                    onFocus={e => { const r = stripCommas(e.target.value); setEF('cost_price', r ? String(r) : ''); }}
                    placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Cost Currency</label>
                  <TypeSearch
                    options={currencies}
                    value={currencies.find(c => c.id === editForm.cost_currency_id)?.code || ''}
                    displayKey="code" placeholder="Currency…"
                    onSelect={c => setEF('cost_currency_id', c.id)}
                    onClear={() => setEF('cost_currency_id', '')}
                  />
                </div>
              </>)}

              <div className="form-group full-width" style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sale Details</div>
              </div>
              <div className="form-group">
                <label className="form-label">Sell Price</label>
                <input className="form-input" inputMode="decimal" value={editForm.sell_price}
                  onChange={e => setEF('sell_price', e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={e => { const f = fmtComma(e.target.value); if (f) setEF('sell_price', f); }}
                  onFocus={e => { const r = stripCommas(e.target.value); setEF('sell_price', r ? String(r) : ''); }}
                  placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Sell Currency</label>
                <TypeSearch
                  options={currencies}
                  value={currencies.find(c => c.id === editForm.sell_currency_id)?.code || ''}
                  displayKey="code" placeholder="Currency…"
                  onSelect={c => setEF('sell_currency_id', c.id)}
                  onClear={() => setEF('sell_currency_id', '')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Status</label>
                <select className="form-input" value={editForm.payment_status} onChange={e => setEF('payment_status', e.target.value)}>
                  {['UNPAID','PAID','PARTIAL'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">FX Rate → LKR</label>
                <input className="form-input" inputMode="decimal" value={editForm.exchange_rate} onChange={e => setEF('exchange_rate', e.target.value)} placeholder="1" />
              </div>
              {!isAgent && (() => {
                const editSell = stripCommas(editForm.sell_price);
                const editCost = stripCommas(editForm.cost_price);
                const editGp = editSell - editCost;
                const editGpPct = editSell > 0 ? ((editGp / editSell) * 100).toFixed(1) : 0;
                return editSell > 0 && editCost > 0 ? (
                  <div className="form-group">
                    <label className="form-label">Gross Profit</label>
                    <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: '1px solid var(--border)', fontWeight: 700, color: editGp >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {editGp.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({editGpPct}%)
                    </div>
                  </div>
                ) : null;
              })()}
              <div className="form-group full-width"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={editForm.notes} onChange={e => setEF('notes', e.target.value)} /></div>

              <div className="form-group full-width" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              {[['Client', ticket.clients?.full_name], ['Ticket No', ticket.ticket_number], ['Departure', ticket.departure_date ? new Date(ticket.departure_date).toLocaleString() : ''], ['Class', ticket.cabin_class], ['Status', ticket.ticket_status], ['Payment', ticket.payment_status], ['Notes', ticket.notes]].map(([l, v]) => v ? (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ) : null)}
              <div style={{ padding: '10px 0 2px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Flight Segments</div>
              {(ticket.segments?.length ? ticket.segments : (ticket.airline ? [{ airline: ticket.airline, pnr: ticket.pnr, route_from: ticket.route_from, route_to: ticket.route_to }] : [])).map((seg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Segment {i + 1} — {seg.airline}{seg.pnr ? ` (PNR ${seg.pnr})` : ''}</span>
                  <span style={{ fontWeight: 500 }}>{seg.route_from && seg.route_to ? `${seg.route_from} → ${seg.route_to}` : '—'}</span>
                </div>
              ))}
            </div>
            <div className="card">
              {(isAgent
                ? [['Sell', `${ticket.sell_currency?.code || ''} ${parseFloat(ticket.sell_price || 0).toLocaleString()}`], ['Created By', ticket.created_by]]
                : [['Cost', `${ticket.cost_currency?.code || ''} ${parseFloat(ticket.cost_price || 0).toLocaleString()}`], ['Sell', `${ticket.sell_currency?.code || ''} ${parseFloat(ticket.sell_price || 0).toLocaleString()}`], ['Gross Profit', parseFloat(ticket.gross_profit || 0).toLocaleString()], ['GP %', ticket.gp_margin_percent ? ticket.gp_margin_percent + '%' : ''], ['Created By', ticket.created_by]]
              ).map(([l, v]) => v ? (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ) : null)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">New Ticket</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Create Ticket'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      <div className="card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Client *</label>
            <TypeSearch
              fetchFn={async (q) => { const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).order('full_name').limit(30); return data || []; }}
              value={clientLabel}
              displayKey="full_name"
              placeholder="Type client name or code…"
              onSelect={c => { setClientLabel(c.full_name); set('client_id', c.id); }}
              onClear={() => { setClientLabel(''); set('client_id', ''); }}
            />
          </div>
          <div className="form-group"><label className="form-label">Ticket Number</label><input className="form-input" value={form.ticket_number} onChange={e => set('ticket_number', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Departure</label><input className="form-input" type="datetime-local" value={form.departure_date} onChange={e => set('departure_date', e.target.value)} /></div>

          {/* Flight segments — most bookings are a single leg, but connections need
              their own airline/PNR/route per leg (e.g. EK CMB→DXB, then UL DXB→KUL). */}
          <div className="form-group full-width" style={{ marginTop: 4 }}>
            <label className="form-label">Flight Segments *</label>
            {segments.map((seg, i) => (
              <div key={seg.key} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr 0.8fr auto', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                <TypeSearch
                  fetchFn={async (q) => {
                    if (!q || q.length < 2) return [];
                    const { data } = await supabase.from('airlines').select('iata_code, name').or(`name.ilike.%${q}%,iata_code.ilike.%${q}%`).eq('is_active', true).limit(12);
                    return (data || []).map(a => ({ ...a, id: a.iata_code, label: `${a.name} (${a.iata_code})` }));
                  }}
                  value={seg.airline}
                  displayKey="label"
                  placeholder={`Segment ${i + 1} airline — e.g. Emirates or EK`}
                  minChars={2}
                  onSelect={a => updateSegment(seg.key, 'airline', `${a.name} (${a.iata_code})`)}
                  onClear={() => updateSegment(seg.key, 'airline', '')}
                  renderOption={(a) => <><strong style={{ fontSize: 13 }}>{a.iata_code}</strong><span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-muted)' }}>{a.name}</span></>}
                />
                <input className="form-input" value={seg.pnr} onChange={e => updateSegment(seg.key, 'pnr', e.target.value)} placeholder="PNR" />
                <input className="form-input" value={seg.route_from} onChange={e => updateSegment(seg.key, 'route_from', e.target.value.toUpperCase())} placeholder="From (CMB)" />
                <input className="form-input" value={seg.route_to} onChange={e => updateSegment(seg.key, 'route_to', e.target.value.toUpperCase())} placeholder="To (DXB)" />
                {segments.length > 1 && (
                  <button type="button" onClick={() => removeSegment(seg.key)}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={addSegment}>+ Add Segment</button>
          </div>
          <div className="form-group">
            <label className="form-label">Cabin Class</label>
            <TypeSearch
              options={['ECONOMY','BUSINESS','FIRST','PREMIUM_ECONOMY'].map(c => ({ id: c, label: c }))}
              value={form.cabin_class}
              displayKey="label"
              placeholder="Select class…"
              onSelect={c => set('cabin_class', c.id)}
            />
          </div>
          {/* ── Purchase (cost side) — hidden for agents ── */}
          {!isAgent && (
            <>
              <div className="form-group full-width" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Purchase Details</div>
              </div>
              <div className="form-group">
                <label className="form-label">Purchased From <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(your IATA BSP, Voyzant, consolidator…)</span></label>
                <TypeSearch
                  options={suppliers}
                  value={form.supplier_label}
                  displayKey="name"
                  placeholder="Type supplier name…"
                  onSelect={s => { set('supplier_id', s.id); set('supplier_label', s.name); }}
                  onClear={() => { set('supplier_id', ''); set('supplier_label', ''); }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Cost Per Pax {totalPax > 1 && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>× {totalPax} pax = {(stripCommas(form.cost_price) * totalPax).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}</label>
                <input className="form-input" inputMode="decimal" value={form.cost_price}
                  onChange={e => setWithAutoComm('cost_price', e.target.value.replace(/[^0-9.]/g, ''))}
                  onBlur={e => { const f = fmtComma(e.target.value); if (f) set('cost_price', f); }}
                  onFocus={e => { const r = stripCommas(e.target.value); set('cost_price', r ? String(r) : ''); }}
                  placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Cost Currency</label>
                <TypeSearch
                  options={currencies}
                  value={currencies.find(c => c.id === form.cost_currency_id)?.code || ''}
                  displayKey="code"
                  placeholder="Currency…"
                  onSelect={c => set('cost_currency_id', c.id)}
                  onClear={() => set('cost_currency_id', '')}
                />
              </div>
              {(() => {
                const costCode = currencies.find(c => c.id === form.cost_currency_id)?.code;
                const sellCode = currencies.find(c => c.id === form.sell_currency_id)?.code;
                const needsRate = (costCode && costCode !== 'LKR') || (sellCode && sellCode !== 'LKR');
                return needsRate ? (
                  <div className="form-group">
                    <label className="form-label">FX Rate → LKR <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(for the foreign amount)</span></label>
                    <input className="form-input" type="text" inputMode="decimal" value={form.exchange_rate} onChange={e => set('exchange_rate', e.target.value)} placeholder="e.g. 320" />
                  </div>
                ) : null;
              })()}
            </>
          )}

          {/* ── Sale (revenue side) ── */}
          <div className="form-group full-width" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Sale Details — Passengers / Line Items</div>
          </div>

          {/* Line items table */}
          <div className="form-group full-width">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg)' }}>
                  <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>Passenger Name</th>
                  <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', width: 150 }}>Ticket Number</th>
                  <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', width: 150 }}>Sell Price</th>
                  <th style={{ width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {lineItems.map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13 }}
                        value={it.description}
                        onChange={e => updateLineItem(i, 'description', e.target.value)}
                        placeholder="Passenger name" />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13 }}
                        value={it.ticketNumber || ''}
                        onChange={e => updateLineItem(i, 'ticketNumber', e.target.value)}
                        placeholder="e.g. 2438123456" />
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <input className="form-input" inputMode="decimal" style={{ fontSize: 13, textAlign: 'right' }}
                        value={it.unitPrice}
                        onChange={e => updateLineItem(i, 'unitPrice', e.target.value.replace(/[^0-9.]/g, ''))}
                        onBlur={() => blurLineItem(i, 'unitPrice')}
                        onFocus={() => focusLineItem(i, 'unitPrice')}
                        placeholder="0.00" />
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                      {lineItems.length > 1 && (
                        <button type="button" onClick={() => removeLineItem(i)}
                          style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={addLineItem}>+ Add Passenger</button>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Total Sell: {totalSell.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Sell Currency</label>
            <TypeSearch
              options={currencies}
              value={currencies.find(c => c.id === form.sell_currency_id)?.code || ''}
              displayKey="code"
              placeholder="Currency…"
              onSelect={c => set('sell_currency_id', c.id)}
              onClear={() => set('sell_currency_id', '')}
            />
          </div>
          {!isAgent && totalSell > 0 && form.cost_price && (
            <div className="form-group">
              <label className="form-label">GP</label>
              <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: '1px solid var(--border)', fontWeight: 700, color: gp >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {gp.toLocaleString()} ({gpPct}%)
              </div>
            </div>
          )}
          {!isAgent && totalSell > 0 && form.cost_price && form.agent_commission && (
            <div className="form-group">
              <label className="form-label">Net GP <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}>after commission</span></label>
              <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: `1px solid ${netGp >= 0 ? 'var(--success)' : 'var(--danger)'}`, fontWeight: 700, color: netGp >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {netGp.toLocaleString()} ({netGpPct}%)
              </div>
            </div>
          )}
          {!isAgent && (
            <div className="form-group">
              <label className="form-label">Care of (Agent)</label>
              <TypeSearch
                options={agents}
                value={form.care_of_agent_label}
                displayKey="label"
                placeholder="TLS code or agent name…"
                onSelect={a => { set('care_of_agent_id', a.id); set('care_of_agent_label', a.label); }}
                onClear={() => { set('care_of_agent_id', ''); set('care_of_agent_label', ''); }}
                renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>}
              />
            </div>
          )}
          {!isAgent && commBlocks.length > 0 && (
            <div className="form-group">
              <label className="form-label">Commission Block</label>
              <select className="form-input" value={selectedBlock} onChange={e => {
                const newBlock = e.target.value;
                setSelectedBlock(newBlock);
                setForm(f => ({ ...f, agent_commission: calcAutoComm(f.sell_price, f.cost_price, f.agent_ref_id, newBlock) }));
              }}>
                <option value="">— Select block —</option>
                {commBlocks.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          {!isAgent && (
            <div className="form-group">
              <label className="form-label">Agent Commission (LKR)</label>
              <TypeSearch
                options={agents}
                value={form.agent_ref_label}
                displayKey="label"
                placeholder="TLS code or agent name…"
                onSelect={a => { setWithAutoComm('agent_ref_id', a.id); set('agent_ref_label', a.label); }}
                onClear={() => { set('agent_ref_id', ''); set('agent_ref_label', ''); set('agent_commission', ''); }}
                renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>}
              />
            </div>
          )}
          {!isAgent && (
            <div className="form-group">
              <label className="form-label">Commission Amount (LKR) {form.agent_ref_id && form.agent_commission ? <span style={{ fontWeight: 400, color: 'var(--primary)', fontSize: 12 }}>— auto-calculated from commission table</span> : ''}</label>
              <input className="form-input" type="text" inputMode="decimal" value={form.agent_commission} onChange={e => set('agent_commission', e.target.value)} placeholder="0.00" disabled={!form.agent_ref_id} />
            </div>
          )}
          <div className="form-group full-width"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}
